import { api, API_URL, authHeaders, resolvePath } from '../config/api';
import { later, withEmbedMock } from './embed';
import * as mock from './dropGroupsMock';

// ---- Drops v2: a drop with a name, a description and several products ----
//
// Contract: outfino-web-contracts/portal-onboarding-drops.md §4 (API
// feature/drop-groups) and §3b (editing a manual product).
//
//   GET    /drops/groups/brand/:brandId      viewProducts   → DropGroup[]
//   POST   /drops/groups                     editProducts   { brand_id, name, description?, startsAt, productIds }
//   PUT    /drops/groups/:groupId            editProducts   any of { name, description, startsAt, productIds }
//   DELETE /drops/groups/:groupId            editProducts
//   PUT    /brands/:brandId/products/manual/:productId (multipart) editProducts
//
// `mode` and `endsAt` are never sent: the API's defaults apply (exclusive;
// 24 hours from the start) — the CEO took "Hol jelenjen meg" and
// "Saját befejezés" out of the form.
//
// Envelope { message: { success, data | error, field?, product_id? } }; api()
// unwraps `message`, leaving { success, data }.
//
// TEST DATA (api/dropGroupsMock.js), like the embed pages: outside production
// builds when the list endpoint answers 404 (or always with
// REACT_APP_EMBED_MOCK=1). The loader resolves to { data, mock }; the writes
// take that flag so a page on test data keeps writing to the test data.

const dataOf = (res) => (res && typeof res === 'object' && 'data' in res ? res.data : res);

export function fetchDropGroups(brandId) {
  return withEmbedMock(
    async () => {
      const list = dataOf(await api(`/drops/groups/brand/${brandId}`));
      return Array.isArray(list) ? list : [];
    },
    later(() => mock.mockList(brandId))
  );
}

/// The create / update body: trimmed; never `mode` or `endsAt`.
export function dropGroupBody({ name, description, startsAt, productIds }) {
  const body = {};
  if (name !== undefined) body.name = String(name).trim();
  if (description !== undefined) body.description = description && String(description).trim() ? String(description).trim() : null;
  if (startsAt !== undefined) body.startsAt = startsAt;
  if (productIds !== undefined) body.productIds = productIds;
  return body;
}

export async function createDropGroup(brandId, fields, { mock: useMock = false, products = [] } = {}) {
  const body = { brand_id: brandId, ...dropGroupBody(fields) };
  if (useMock) return later(() => mock.mockCreate(brandId, body, products))();
  return dataOf(await api('/drops/groups', { method: 'POST', body }));
}

export async function updateDropGroup(groupId, fields, { mock: useMock = false, products = [] } = {}) {
  const body = dropGroupBody(fields);
  if (useMock) return later(() => mock.mockUpdate(groupId, body, products))();
  return dataOf(await api(`/drops/groups/${groupId}`, { method: 'PUT', body }));
}

export async function cancelDropGroup(groupId, { mock: useMock = false } = {}) {
  if (useMock) return later(() => mock.mockUpdate(groupId, { status: 'cancelled' }))();
  return dataOf(await api(`/drops/groups/${groupId}`, { method: 'PUT', body: { status: 'cancelled' } }));
}

export async function deleteDropGroup(groupId, { mock: useMock = false } = {}) {
  if (useMock) return later(() => mock.mockDelete(groupId))();
  return dataOf(await api(`/drops/groups/${groupId}`, { method: 'DELETE' }));
}

// ---- Editing a manual product (§3b) ----

/// Only what changed: text fields, `sizes` (replaces; '[]' clears), new
/// `images` (added after the kept ones), `removeImages` (the photos' src).
export function buildProductEditForm({ name, price, currency, sizes, description, images, removeImages, replaceImages } = {}) {
  const form = new FormData();
  if (name !== undefined) form.append('name', name);
  if (price !== undefined) form.append('price', price);
  if (currency !== undefined) form.append('currency', currency);
  if (description !== undefined) form.append('description', description);
  if (sizes !== undefined) {
    if (sizes.length === 0) form.append('sizes', '[]');
    else sizes.forEach((s) => form.append('sizes', s));
  }
  (removeImages || []).forEach((src) => form.append('removeImages', src));
  if (replaceImages) form.append('replaceImages', 'true');
  (images || []).forEach((file) => form.append('images', file));
  return form;
}

/// → { product, labels, reanalyzed, isClothing, usable, reason, affectedDrops }.
/// Test data: when the route is not there yet (a 404 without a known code)
/// outside production builds, the change is applied locally.
export async function updateManualProduct(brandId, product, changes, { mockAllowed = true } = {}) {
  const res = await fetch(`${API_URL}${resolvePath(`/brands/${brandId}/products/manual/${product.id}`, 'PUT')}`, {
    method: 'PUT',
    headers: await authHeaders(),
    body: buildProductEditForm(changes),
  });
  let data = null;
  try {
    data = await res.json();
  } catch {
    // no JSON
  }
  if (res.ok) return data && typeof data === 'object' && 'message' in data ? data.message : data;
  const code = typeof data?.error === 'string' ? data.error : null;
  if (res.status === 404 && !code && mockAllowed && mock.mockAllowed()) {
    return later(() => mock.mockEditProduct(product, changes))();
  }
  const err = new Error(code || `HTTP ${res.status}`);
  err.status = res.status;
  err.data = data;
  throw err;
}
